/*
 * Gift Redemption — Resend controller
 * --------------------------------------------------------------------------
 * Powers the footer "Redeem a gift" resend page (sc-736497, entry path 2).
 * Takes a recipient email and POSTs it to the barkbox-rails resend endpoint,
 * which re-fires the original gift redemption email if an unredeemed gift
 * matches. See handoff 2026-07-08_barkbox-rails__gift-subscription-resend-endpoint.md.
 *
 * NON-ENUMERABLE ENDPOINT (password-reset pattern): the endpoint returns 200 for
 * ANY non-blank email (422 only for a blank one) and never reveals whether a gift
 * exists. So this controller shows a single NEUTRAL confirmation on success and
 * must never render "gift found / not found."
 *
 * The Rails origin is server-injected via the bark.barkapi_base_url shop
 * metafield and read from the container's data-bark-api-base-url attribute (same
 * source the DD / theme-swap selectors use). This endpoint is public, so — unlike
 * BarkApiClientBase — we send a bare JSON POST with no Authorization header.
 *
 * Namespaced under window.GiftResend, decoupled from GiftFunnel / SubscriptionFunnel.
 */
(function () {
  window.GiftResend = window.GiftResend || {};
  var GiftResend = window.GiftResend;

  // Appended to the barkapi base URL. Absolute path, matching the base-URL +
  // absolute-path composition the selector clients use.
  var RESEND_PATH = "/gift_subscriptions/resend";

  // Mirror the funnel email regex (assets/funnel-step-email.js) for consistency.
  var EMAIL_REGEX =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;

  GiftResend.els = {};

  GiftResend.init = function () {
    var root = document.querySelector("[data-gift-resend]");
    if (!root) return;

    GiftResend.root = root;
    GiftResend.baseUrl = (root.dataset.barkApiBaseUrl || "").replace(/\/+$/, "");

    GiftResend.els = {
      form: root.querySelector("[data-gift-resend-form-wrapper]"),
      confirmation: root.querySelector("[data-gift-resend-confirmation]"),
      email: root.querySelector("#gift-resend-email"),
      emailError: root.querySelector("#gift-resend-email-error"),
      requestError: root.querySelector("[data-gift-resend-request-error]"),
      button: root.querySelector("[data-gift-resend-submit]"),
    };

    var email = GiftResend.els.email;
    if (email) {
      email.addEventListener("input", GiftResend.clearErrors);
      email.addEventListener("keydown", function (event) {
        if (event.key === "Enter") {
          event.preventDefault();
          GiftResend.submit();
        }
      });
    }
  };

  GiftResend.validateEmail = function (email) {
    return EMAIL_REGEX.test(email);
  };

  GiftResend.clearErrors = function () {
    var els = GiftResend.els;
    if (els.emailError) els.emailError.classList.add("display-none");
    if (els.email) {
      els.email.classList.remove("text-input-component-input--error");
      els.email.setAttribute("aria-invalid", "false");
    }
    if (els.requestError) els.requestError.hidden = true;
  };

  GiftResend.showEmailError = function (message) {
    var els = GiftResend.els;
    if (els.emailError) {
      els.emailError.textContent = message;
      els.emailError.classList.remove("display-none");
    }
    if (els.email) {
      els.email.classList.add("text-input-component-input--error");
      els.email.setAttribute("aria-invalid", "true");
    }
  };

  GiftResend.showRequestError = function () {
    if (GiftResend.els.requestError) GiftResend.els.requestError.hidden = false;
  };

  GiftResend.setLoading = function (loading) {
    var button = GiftResend.els.button;
    if (!button) return;
    button.disabled = loading;
    var text = button.querySelector(".button-text");
    var loader = button.querySelector(".loader");
    if (text) text.classList.toggle("display-none", loading);
    if (loader) loader.classList.toggle("display-none", !loading);
  };

  GiftResend.submit = function () {
    var els = GiftResend.els;
    if (!els.email) return;

    var email = els.email.value.trim();
    GiftResend.clearErrors();

    // Client-side validation. The endpoint only 422s on a blank email; catching
    // blanks (and obvious typos) here keeps the request well-formed. We do NOT
    // treat a "no matching gift" as an error — the endpoint can't tell us that.
    if (email === "") {
      GiftResend.showEmailError("Error: Required field.");
      els.email.focus();
      return;
    }
    if (!GiftResend.validateEmail(email)) {
      GiftResend.showEmailError("Error: Valid email address required.");
      els.email.focus();
      return;
    }

    if (!GiftResend.baseUrl) {
      // Metafield missing/misconfigured — we have nowhere to send the request.
      console.error(
        "Gift resend: missing shop.metafields.bark.barkapi_base_url — cannot resend"
      );
      GiftResend.showRequestError();
      return;
    }

    GiftResend.setLoading(true);

    // Cross-origin POST to the Rails host. Requires the endpoint to send CORS
    // headers for the storefront origin and to exempt this route from CSRF —
    // both are barkbox-rails-side concerns (handoff open questions). If CORS is
    // absent the fetch rejects and the recipient sees the retry error even
    // though Rails may have accepted the request.
    fetch(GiftResend.baseUrl + RESEND_PATH, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ email: email }),
    })
      .then(function (res) {
        // Any 2xx = "request accepted" (email may or may not exist). A non-2xx is
        // a genuine failure worth a retry prompt. A blank-email 422 shouldn't
        // reach here because of client validation above.
        if (!res.ok) throw new Error("Resend failed: " + res.status);
      })
      .then(function () {
        GiftResend.showConfirmation();
      })
      .catch(function (err) {
        console.error("Gift resend request failed", err);
        GiftResend.setLoading(false);
        GiftResend.showRequestError();
      });
  };

  GiftResend.showConfirmation = function () {
    var els = GiftResend.els;
    // Swap the form out for the neutral confirmation. Leaving the form gone also
    // serves as light client-side rate limiting — no resubmitting from this view.
    if (els.form) els.form.classList.add("display-none");
    if (els.confirmation) {
      els.confirmation.classList.remove("display-none");
      if (typeof els.confirmation.focus === "function") {
        els.confirmation.focus();
      }
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", GiftResend.init);
  } else {
    GiftResend.init();
  }
})();
